import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

/**
 * Why Skills? — the brand manifesto. Three short paragraphs explaining
 * why transferable skills matter. Used in two places:
 *
 *  · `WhySkillsHover` — the desktop pill that pops a HoverCard on
 *    hover. `hidden md:inline-flex` so the pill is desktop-only;
 *    HoverCard doesn't work for touch anyway.
 *  · `WhySkillsMobileItem` — a mobile-only DropdownMenuItem that
 *    opens the same content in a tap-friendly Dialog. Wired into the
 *    hamburger menu in `UserMenu.jsx`.
 *
 * Both share `WhySkillsPanel` so the copy lives in one place.
 */

export const WhySkillsPanel = () => (
  <div className="space-y-4 text-[14px] leading-[1.7] font-medium">
    <p data-testid="why-skills-p1">
      The majority of hiring failures have nothing to do with technical
      incompetence. Employers aren&apos;t turning people away because they
      can&apos;t do the job. They&apos;re turning people away because they
      can&apos;t think through a problem, work inside a team, or recover
      when something goes sideways.
    </p>
    <p data-testid="why-skills-p2">
      Those are transferable skills. And they&apos;re built — or not built —
      in Grades 4 to 8.
    </p>
    <p data-testid="why-skills-p3">
      Ontario&apos;s curriculum names them. The Ministry believes in them.
      But no one has ever given teachers the tools to actually teach them —{" "}
      <strong
        data-testid="why-skills-emphasis"
        className="font-extrabold text-[#96ded1]"
      >
        until now.
      </strong>
    </p>
  </div>
);

export const WhySkillsHover = () => (
  <HoverCard openDelay={120} closeDelay={120}>
    <HoverCardTrigger asChild>
      <button
        type="button"
        data-testid="why-skills-trigger"
        aria-label="Why skills?"
        className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#96ded1]/40 bg-[#96ded1]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#96ded1] hover:bg-[#96ded1]/15 hover:border-[#96ded1]/70 focus:outline-none focus:ring-2 focus:ring-[#96ded1]/40 transition-colors cursor-help"
      >
        Why skills?
      </button>
    </HoverCardTrigger>
    <HoverCardContent
      data-testid="why-skills-popover"
      align="end"
      sideOffset={10}
      className="w-[460px] max-w-[90vw] border border-[#1c2d3d]/40 p-0 shadow-2xl rounded-xl overflow-hidden bg-[#1c2d3d]"
    >
      <div className="px-6 py-6 text-white/85">
        <WhySkillsPanel />
      </div>
    </HoverCardContent>
  </HoverCard>
);

/**
 * Mobile-only Dialog body — pair with a controlled-open prop so the
 * caller can trigger it from a DropdownMenuItem (which auto-closes
 * its parent dropdown on select, giving a clean single-modal effect).
 */
export const WhySkillsMobileDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid="why-skills-mobile-dialog"
      className="max-w-md bg-[#1c2d3d] text-white/85 border border-[#1c2d3d]/40 p-6"
    >
      <DialogHeader>
        <DialogTitle className="text-[#96ded1] text-base font-extrabold uppercase tracking-[0.18em]">
          Why skills?
        </DialogTitle>
        <DialogDescription className="sr-only">
          The Atomic Edu brand manifesto on transferable skills.
        </DialogDescription>
      </DialogHeader>
      <WhySkillsPanel />
    </DialogContent>
  </Dialog>
);

export default WhySkillsHover;
